import { ReactElement, useMemo, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import IconButton from '@mui/material/IconButton'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import EditIcon from '@mui/icons-material/Edit'
import SaveIcon from '@mui/icons-material/Save'
import CloseIcon from '@mui/icons-material/Close'
import AddIcon from '@mui/icons-material/Add'
import { heapEvent } from '@equipmentshare/heaper'
import {
	ItemMasterReadinessQuery,
	ItemMasterVariantUom,
	useItemMasterReadinessQuery,
	useUpdateStorePartMutation,
	useCreateStorePartMutation,
} from '../../../../../../graphql'
import { useShowError, useShowSuccess } from 'hooks/useToast'
import AddToDistributionCenterDialog from './AddToDistributionCenterDialog'

type StorePartNode = ItemMasterReadinessQuery['storeParts'][number]

export type VariantInfo = {
	id: string,
	isBase: boolean,
	uom: ItemMasterVariantUom,
	productId: string | null,
}

type VariantSlot = {
	variant: VariantInfo,
	storePart: StorePartNode | null,
	location: string,
	isReady: boolean,
}

type DCGroup = {
	id: string,
	name: string,
	branchName: string,
	slots: VariantSlot[],
	readyCount: number,
	totalVariants: number,
}

type Props = {
	itemMasterId: string,
	variants: VariantInfo[],
	// When true, the item master is a draft (missing weight/dimensions) and may not
	// be connected to a distribution center. Disables Add to DC and Start tracking.
	incomplete?: boolean,
}

const INCOMPLETE_DC_HINT = 'Add weight and dimensions to complete this item master before adding it to a distribution center.'

function ReadyChip( { ready }: { ready: boolean } ): ReactElement {
	if ( ready ) {
		return (
			<Tooltip title="Numina can see this item at this distribution center">
				<Chip icon={<CheckCircleIcon />} label="Ready" color="success" size="small" />
			</Tooltip>
		)
	}
	return (
		<Tooltip title="To make this distribution center ready, set a bin location for at least one variant below.">
			<Chip icon={<ErrorOutlineIcon />} label="Not ready" color="default" size="small" variant="outlined" />
		</Tooltip>
	)
}

function VariantStatusChip( { ready, tracked }: { ready: boolean, tracked: boolean } ): ReactElement {
	if ( ready ) {
		return (
			<Tooltip title="Bin location is set — Numina can see this variant here.">
				<Chip label="Ready" color="success" size="small" />
			</Tooltip>
		)
	}
	const hint = tracked
		? 'Set a bin location for this variant to make it ready.'
		: 'Click Start tracking to begin tracking this variant at this distribution center, then set its bin location.'
	return (
		<Tooltip title={hint}>
			<Chip label="Not ready" color="default" size="small" variant="outlined" />
		</Tooltip>
	)
}

export default function NuminaReadinessSection( { itemMasterId, variants, incomplete = false }: Props ): ReactElement {
	const variantPartIds = useMemo( () => {
		return variants
			.map( ( v ) => Number( v.productId ) )
			.filter( ( n ) => Number.isFinite( n ) )
	}, [ variants ] )

	const skip = variantPartIds.length === 0

	const { data, loading, refetch } = useItemMasterReadinessQuery( {
		variables: { partIds: variantPartIds },
		skip,
		fetchPolicy: 'cache-and-network',
	} )

	const [ updateStorePart, { loading: updating } ] = useUpdateStorePartMutation()
	const [ createStorePart, { loading: creating } ] = useCreateStorePartMutation()

	const [ editingStorePartId, setEditingStorePartId ] = useState<number | null>( null )
	const [ locationDraft, setLocationDraft ] = useState( '' )
	const [ dialogOpen, setDialogOpen ] = useState( false )

	const showSuccess = useShowSuccess()
	const showError = useShowError()

	const dcGroups: DCGroup[] = useMemo( () => {
		const storeParts = data?.storeParts ?? []
		const byLocation = new Map<string, { name: string, branchName: string, byPartId: Map<string, StorePartNode> }>()

		for ( const sp of storeParts ) {
			const locId = String( sp.inventoryLocationId )
			if ( !byLocation.has( locId ) ) {
				byLocation.set( locId, {
					name: sp.inventoryLocation?.name ?? '',
					branchName: sp.inventoryLocation?.branch?.name ?? '',
					byPartId: new Map(),
				} )
			}
			byLocation.get( locId )!.byPartId.set( String( sp.partId ), sp )
		}

		const groups: DCGroup[] = []
		for ( const [ locId, info ] of byLocation.entries() ) {
			const slots: VariantSlot[] = variants.map( ( v ) => {
				const sp = v.productId ? info.byPartId.get( String( v.productId ) ) ?? null : null
				const location = sp?.location ?? ''
				return {
					variant: v,
					storePart: sp,
					location,
					isReady: Boolean( location.trim() ),
				}
			} )
			const readyCount = slots.filter( ( s ) => s.isReady ).length
			groups.push( {
				id: locId,
				name: info.name,
				branchName: info.branchName,
				slots,
				readyCount,
				totalVariants: slots.length,
			} )
		}
		return groups.sort( ( a, b ) => a.name.localeCompare( b.name ) )
	}, [ data, variants ] )

	const trackedLocationIds = useMemo( () => {
		return new Set( dcGroups.map( ( g ) => g.id ) )
	}, [ dcGroups ] )

	const totalDCs = dcGroups.length
	const readyDCs = dcGroups.filter( ( g ) => g.readyCount > 0 ).length

	const beginEdit = ( slot: VariantSlot ) => {
		if ( !slot.storePart ) {
			return
		}
		setEditingStorePartId( slot.storePart.storePartId )
		setLocationDraft( slot.location )
	}

	const cancelEdit = () => {
		setEditingStorePartId( null )
		setLocationDraft( '' )
	}

	const saveLocation = async( slot: VariantSlot, dcId: string ) => {
		if ( !slot.storePart ) {
			return
		}
		const next = locationDraft.trim()
		try {
			const { data: mutationData } = await updateStorePart( {
				variables: {
					storePartId: slot.storePart.storePartId,
					location: next || null,
				},
			} )
			if ( !mutationData?.updateStorePart?.success ) {
				throw new Error( mutationData?.updateStorePart?.message ?? 'Failed to save bin location.' )
			}
			heapEvent( 'Inventory - Item Master - Detail - Bin Location - Submit - Success', {
				item_master_id: itemMasterId,
				inventory_location_id: dcId,
			} )
			showSuccess( `binLocationSuccess${Date.now()}`, 'Bin location updated.' )
			setEditingStorePartId( null )
			setLocationDraft( '' )
			await refetch()
		} catch ( err ) {
			heapEvent( 'Inventory - Item Master - Detail - Bin Location - Submit - Error', {
				item_master_id: itemMasterId,
				inventory_location_id: dcId,
			} )
			const message = err instanceof Error ? err.message : 'Failed to save bin location.'
			showError( `binLocationError${Date.now()}`, message )
		}
	}

	const startTrackingVariant = async( slot: VariantSlot, dcId: string ) => {
		if ( !slot.variant.productId ) {
			return
		}
		try {
			const { data: mutationData } = await createStorePart( {
				variables: {
					input: {
						productId: slot.variant.productId,
						inventoryLocationId: dcId,
					},
				},
			} )
			if ( !mutationData?.createStorePart?.success ) {
				throw new Error( mutationData?.createStorePart?.message ?? 'Failed to start tracking.' )
			}
			await refetch()
		} catch ( err ) {
			const message = err instanceof Error ? err.message : 'Failed to start tracking.'
			showError( `startTrackingError${Date.now()}`, message )
		}
	}

	const openDialog = () => {
		heapEvent( 'Inventory - Item Master - Detail - Click - Add To DC', { item_master_id: itemMasterId } )
		setDialogOpen( true )
	}

	const handleAdded = async() => {
		await refetch()
	}

	const renderBinCell = ( slot: VariantSlot, dcId: string ) => {
		if ( !slot.storePart ) {
			return (
				<Stack direction="row" spacing={1} alignItems="center">
					<Typography variant="body2" color="text.secondary">Not tracked</Typography>
					<Tooltip title={incomplete ? INCOMPLETE_DC_HINT : 'Start tracking this variant at this distribution center'}>
						<span>
							<Button
								size="small"
								startIcon={<AddIcon />}
								onClick={() => startTrackingVariant( slot, dcId )}
								disabled={creating || incomplete}
							>
								Start tracking
							</Button>
						</span>
					</Tooltip>
				</Stack>
			)
		}
		if ( editingStorePartId === slot.storePart.storePartId ) {
			return (
				<Stack direction="row" spacing={1} alignItems="center">
					<TextField
						value={locationDraft}
						onChange={( e ) => setLocationDraft( e.target.value )}
						size="small"
						placeholder="Bin / location"
						autoFocus
						disabled={updating}
						sx={{ minWidth: 180 }}
					/>
					<Tooltip title="Save">
						<span>
							<IconButton size="small" onClick={() => saveLocation( slot, dcId )} disabled={updating}>
								<SaveIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
					<Tooltip title="Cancel">
						<span>
							<IconButton size="small" onClick={cancelEdit} disabled={updating}>
								<CloseIcon fontSize="small" />
							</IconButton>
						</span>
					</Tooltip>
				</Stack>
			)
		}
		return (
			<Stack direction="row" spacing={1} alignItems="center">
				<Typography variant="body2" color={slot.location ? 'text.primary' : 'text.secondary'}>
					{slot.location || 'No location set'}
				</Typography>
				<Tooltip title="Edit location">
					<IconButton size="small" onClick={() => beginEdit( slot )}>
						<EditIcon fontSize="small" />
					</IconButton>
				</Tooltip>
			</Stack>
		)
	}

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
			<Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
				<Stack direction="row" alignItems="baseline" spacing={2}>
					<Typography variant="h6">Numina Readiness</Typography>
					{!loading && totalDCs > 0 && (
						<Typography variant="body2" color="text.secondary">
							{readyDCs} of {totalDCs} distribution {totalDCs === 1 ? 'center' : 'centers'} ready
						</Typography>
					)}
				</Stack>
				<Tooltip title={incomplete ? INCOMPLETE_DC_HINT : ''}>
					<span>
						<Button
							variant="outlined"
							startIcon={<AddIcon />}
							onClick={openDialog}
							disabled={skip || incomplete}
						>
							Add to Distribution Center
						</Button>
					</span>
				</Tooltip>
			</Stack>

			{!loading && totalDCs === 0 ? (
				<Box
					sx={{
						flex: 1,
						minHeight: 240,
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						border: '1px dashed',
						borderColor: 'divider',
						borderRadius: 1,
						p: 4,
						textAlign: 'center',
					}}
				>
					<Typography variant="body1" mb={1}>
						Not carried at any distribution center yet.
					</Typography>
					<Typography variant="body2" color="text.secondary" mb={2}>
						{incomplete
							? INCOMPLETE_DC_HINT
							: 'Add a distribution center to start tracking this item there. Numina can see it once at least one variant\'s bin location is set.'}
					</Typography>
					<Tooltip title={incomplete ? INCOMPLETE_DC_HINT : ''}>
						<span>
							<Button
								variant="contained"
								startIcon={<AddIcon />}
								onClick={openDialog}
								disabled={skip || incomplete}
							>
								Add to Distribution Center
							</Button>
						</span>
					</Tooltip>
				</Box>
			) : (
				<Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
					{dcGroups.map( ( dc ) => {
						const dcReady = dc.readyCount > 0
						return (
							<Paper key={dc.id} variant="outlined" sx={{ p: 2 }}>
								<Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.5} flexWrap="wrap" gap={1}>
									<Stack>
										<Stack direction="row" alignItems="center" spacing={1.5}>
											<Typography variant="subtitle1" fontWeight={600}>{dc.name}</Typography>
											<ReadyChip ready={dcReady} />
										</Stack>
										{dc.branchName && (
											<Typography variant="body2" color="text.secondary">{dc.branchName}</Typography>
										)}
									</Stack>
									<Typography variant="body2" color="text.secondary">
										{dc.readyCount} of {dc.totalVariants} {dc.totalVariants === 1 ? 'bin' : 'bins'} set
									</Typography>
								</Stack>
								<Table size="small">
									<TableHead>
										<TableRow>
											<TableCell>UoM</TableCell>
											<TableCell>Status</TableCell>
											<TableCell>Bin / Location</TableCell>
											<TableCell align="right">Quantity</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{dc.slots.map( ( slot ) => (
											<TableRow key={slot.variant.id}>
												<TableCell>
													<Stack direction="row" alignItems="center" spacing={1}>
														<Typography variant="body2">{slot.variant.uom}</Typography>
														{slot.variant.isBase && (
															<Chip label="Base" size="small" color="primary" variant="outlined" />
														)}
													</Stack>
												</TableCell>
												<TableCell>
													<VariantStatusChip ready={slot.isReady} tracked={Boolean( slot.storePart )} />
												</TableCell>
												<TableCell>{renderBinCell( slot, dc.id )}</TableCell>
												<TableCell align="right">
													{slot.storePart ? slot.storePart.quantity ?? 0 : '—'}
												</TableCell>
											</TableRow>
										) )}
									</TableBody>
								</Table>
							</Paper>
						)
					} )}
				</Stack>
			)}

			<AddToDistributionCenterDialog
				open={dialogOpen}
				itemMasterId={itemMasterId}
				variantProductIds={variants
					.map( ( v ) => v.productId )
					.filter( ( id ): id is string => Boolean( id ) )}
				trackedInventoryLocationIds={trackedLocationIds}
				onClose={() => setDialogOpen( false )}
				onAdded={handleAdded}
			/>
		</Box>
	)
}
